<template>
  <div
    v-if="isVideo"
    :class="$style.container"
    @click="(handleTracking($event), (isModalOpen = true))"
  >
    <figure :class="$style.figure">
      <figcaption :class="$style.caption">
        <h3 :class="$style.title" v-html="getTitleWithMaxLength(result)" />
        <div
          v-if="result.highlight && result.highlight.description"
          :class="$style.text"
          v-html="getDescriptionWithMaxLength(result)"
        />
      </figcaption>
      <img
        v-if="result._source?.image"
        :alt="result._source?.title"
        :class="$style.image"
        :src="result._source?.image"
      />
    </figure>
  </div>
  <a
    v-else
    :class="$style.container"
    :href="setLinkUrl(resultUrl, terms)"
    :target="getLinkTarget()"
    @click="handleDownload($event, resultUrl)"
  >
    <figure :class="$style.figure">
      <figcaption :class="$style.caption">
        <h3 :class="$style.title" v-html="getTitleWithMaxLength(result)" />
        <div :class="$style.text" v-html="getDescriptionWithMaxLength(result)" />
      </figcaption>
      <img
        v-if="result._source?.image"
        :alt="result._source?.title"
        :class="$style.image"
        :src="result._source?.image"
      />
    </figure>
    <div :class="style.path">
      <template v-if="result._source?.breadcrumb">
        <span
          v-for="label in result._source?.breadcrumb.split(/\,/g)"
          :key="label"
          v-html="label"
        />
      </template>
      <template v-else>
        <span v-html="result._source?.categoryLabel" />
      </template>
    </div>
  </a>
  <Modal :is-modal-open="isModalOpen" :is-video="isVideo" @close-modal="isModalOpen = false">
    <iframe
      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
      allowfullscreen
      :data-src="resultUrl + '?enablejsapi=1'"
      frameborder="0"
    />
  </Modal>
</template>

<script setup>
// import { defineAsyncComponent } from 'vue';
import Modal from '../Modal/Modal.vue';
import { Category, DocType } from '../../globals/constants/search';
import { endsWith } from '../../utils/helper';
import { useCssModule, ref, onMounted } from 'vue';
import truncate from 'truncate-html';

// const truncate = defineAsyncComponent(() => import('truncate-html'));

const style = useCssModule();

const props = defineProps({
  result: {
    type: Object,
    default: () => {},
  },
  webBaseUrl: {
    type: String,
    default: '',
  },
  shopBaseUrl: {
    type: String,
    default: '',
  },
  textfieldOfActivity: {
    type: String,
    default: '',
  },
  textLocation: {
    type: String,
    default: '',
  },
  textCountry: {
    type: String,
    default: '',
  },
  textTargetGroup: {
    type: String,
    default: '',
  },
  textEmployment: {
    type: String,
    default: '',
  },
  searchMicrosite: {
    type: Boolean,
    default: false,
  },
  terms: {
    type: Array,
    default: () => [],
  },
});

const resultUrl = ref('');
const isModalOpen = ref(false);
const isVideo = ref(false);
const wtTrack = window.wtTrack;

const addDotsToLastElement = (elem) => {
  if (!elem || !elem.lastChild) {
    return;
  }

  if (elem.lastChild.nodeType === 3 && elem.lastChild.nodeValue.trim() === '') {
    elem.removeChild(elem.lastChild);
  }

  const { lastChild } = elem;

  if (lastChild.nodeType === 1) {
    addDotsToLastElement(lastChild);
  } else if (lastChild) {
    if (!endsWith(lastChild.textContent.trim(), '...')) {
      const span = document.createElement('span');
      span.className = style.dots;
      span.innerText = ' ...';
      lastChild.parentNode.appendChild(span);
    }
  }
};

const addTrailingDots = (htmlString) => {
  const parser = new DOMParser();
  const parsedHtml = parser.parseFromString(htmlString, 'text/html');
  addDotsToLastElement(parsedHtml.body);
  return parsedHtml.body.innerHTML;
};

const handleTracking = () => {
  const wtParams = props.result._source?.wtParams;
  wtParams && wtTrack({ cat: wtParams.doctype }, wtParams);
};

const handleDownload = (event, url) => {
  if (url.endsWith('stp')) {
    event.preventDefault();

    const link = document.createElement('a');
    link.href = url;
    link.download = url.split('/').at(-1);

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  }

  handleTracking();
};

const getLinkTarget = () => {
  return (props.result._source?.categoryUid || props.result._source?.categoryuid) ===
    Category.DOWNLOAD || props.result._source?.docType === DocType.BLOG
    ? '_blank'
    : '_self';
};

const getTitleWithMaxLength = (result) => {
  if ((props.result._source?.categoryUid || props.result._source?.categoryuid) === Category.JOBS) {
    if (result.highlight && result.highlight.position) {
      return result.highlight.position.join(' ');
    } else if (result._source?.position) {
      return result._source?.position;
    }
  }

  if (result.highlight && result.highlight.title) {
    return result.highlight.title.join(' ');
  } else if (result._source?.title) {
    return result._source?.title;
  }
};

const getDescriptionWithMaxLength = (result) => {
  if ((props.result._source?.categoryUid || props.result._source?.categoryuid) === Category.JOBS) {
    var returnString = '';

    if (result.highlight && result.highlight.fieldOfActivity) {
      returnString +=
        props.textfieldOfActivity + ': ' + result.highlight.fieldOfActivity.join(' ') + ' - ';
    } else if (result._source?.fieldOfActivity) {
      returnString += props.textfieldOfActivity + ': ' + result._source?.fieldOfActivity + ' - ';
    }

    if (result.highlight && result.highlight.location) {
      returnString += props.textLocation + ': ' + result.highlight.location.join(' ') + ' - ';
    } else if (result._source?.location) {
      returnString += props.textLocation + ': ' + result._source?.location + ' - ';
    }

    if (result.highlight && result.highlight.country) {
      returnString += props.textCountry + ': ' + result.highlight.country.join(' ') + ' - ';
    } else if (result._source?.country) {
      returnString += props.textCountry + ': ' + result._source?.country + ' - ';
    }

    if (result.highlight && result.highlight.targetGroup) {
      returnString += props.textTargetGroup + ': ' + result.highlight.targetGroup.join(' ') + ' - ';
    } else if (result._source?.targetGroup) {
      returnString += props.textTargetGroup + ': ' + result._source?.targetGroup + ' - ';
    }

    if (result.highlight && result.highlight.employment) {
      returnString += props.textEmployment + ': ' + result.highlight.employment.join(' ');
    } else if (result._source?.employment) {
      returnString += props.textEmployment + ': ' + result._source?.employment;
    }

    return returnString || cropTextIfTooLong(result._source?.description);
  } else {
    if (props.searchMicrosite) {
      if (result.highlight && result.highlight.contentUnstemmed) {
        return cropTextIfTooLong(result.highlight.contentUnstemmed.join(' '));
      } else if (result._source?.description) {
        return cropTextIfTooLong(result._source?.description);
      } else {
        return '';
      }
    } else {
      if (result.highlight && result.highlight.description) {
        return cropTextIfTooLong(result.highlight.description.join(' '));
      } else if (result._source?.description) {
        return cropTextIfTooLong(result._source?.description);
      } else {
        return '';
      }
    }
  }
};

const cropTextIfTooLong = (text) => {
  const textWithoutParagraph = text.replace(/<p>/g, '').replace(/<\/p>/g, '');

  if ((props.result._source?.categoryUid || props.result._source?.categoryuid) === 'contacts') {
    return textWithoutParagraph.trim();
  }

  const description = truncate(textWithoutParagraph, 160, {
    ellipsis: '',
    reserveLastWord: true,
  })
    .trim()
    .replace(/[\\.]{2,3}$/gm, '');

  return addTrailingDots(description);
};

const setResultUrlWithOptionalPrefix = (path, baseUrl) => {
  if (path.indexOf('http') === 0) {
    resultUrl.value = props.result._source?.path;
  } else {
    resultUrl.value = baseUrl + props.result._source?.path;
  }
};

const setLinkUrl = (url, term) => {
  try {
    const modifiedUrl = new URL(url);

    // exclude extension to prevent wrong download format
    const excludedFileExtensions = ['stp', 'pdf'];

    for (const excludedFileExtension of excludedFileExtensions) {
      if (modifiedUrl.pathname.endsWith(`.${excludedFileExtension}`)) {
        return url;
      }
    }

    // add highlight
    modifiedUrl.searchParams.append('highlightedText', term.slice(0, 1));

    if (props.result._source?.standardArticle) {
      modifiedUrl.searchParams.append('tab', 'orderdata');
    }

    return modifiedUrl.toString();
  } catch {
    return url;
  }
};

const preprocessResultCategory = () => {
  switch (props.result._source?.categoryUid || props.result._source?.categoryuid) {
    case Category.SHOP:
      setResultUrlWithOptionalPrefix(props.result._source?.path, props.shopBaseUrl);
      break;
    case Category.VIDEOS:
      resultUrl.value = props.result._source?.path;
      isVideo.value = true;
      break;
    case Category.BLOG:
      resultUrl.value = props.result._source?.path;
      break;
    default:
      setResultUrlWithOptionalPrefix(
        props.result._source?.path,
        window.instance === 'shop' ? props.shopBaseUrl : props.webBaseUrl,
      );
      break;
  }
};

onMounted(() => {
  if (props.result._source) {
    preprocessResultCategory();
  }
});
</script>

<style module lang="scss">
@forward './ResultTeaser.scss';
</style>
